import React, { useState, useRef, useEffect, useMemo } from "react";
import { CheckCircle2, Play, MoreVertical, Edit3, Trash2 } from "lucide-react";
import { VideoItem } from "../types/video.js";
import { formatDuration, formatLocalizedViews, formatLocalizedDate } from "../utils/format.js";
import { useI18n } from "../i18n/I18nContext.js";
import { ChannelBadge } from "./common/ChannelBadge.js";
import {
  resolveExperimentVariant,
  recordImpressionTelemetry,
  recordClickTelemetry,
} from "../services/experimentService.js";

export interface VideoCardProps {
  video: VideoItem;
  onClick: (video: VideoItem) => void;
  onEdit?: (video: VideoItem) => void;
  onDelete?: (video: VideoItem) => void;
  onCreatorClick?: (creatorHandleOrName: string, video: VideoItem) => void;
  watchHistory?: { lastPosition: number; duration: number };
}

export const JOVIAL_GOLD_AVATAR = "/assets/jovial-avatar.png";

/** Renders a creator avatar img with an initials or Jovial gold monogram fallback when the image fails to load */
const AvatarWithFallback: React.FC<{
  src?: string;
  name?: string;
  handle?: string;
  channelId?: string;
}> = ({ src, name, handle, channelId }) => {
  const isJovial =
    (name || "").toLowerCase().includes("jovial") ||
    (handle || "").toLowerCase().includes("jovial") ||
    (channelId || "").toLowerCase().includes("jovial") ||
    (channelId || "").includes("1788636226996");

  // If source is missing or contains the dicebear identicon checkerboard, replace immediately for Jovial
  const defaultSrc = isJovial
    ? !src || src.includes("identicon")
      ? JOVIAL_GOLD_AVATAR
      : src
    : src;

  const [currentSrc, setCurrentSrc] = useState<string | undefined>(defaultSrc);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const s = isJovial
      ? !src || src.includes("identicon")
        ? JOVIAL_GOLD_AVATAR
        : src
      : src;
    setCurrentSrc(s);
    setHasError(false);
  }, [src, isJovial]);

  const initials =
    (name || "HX")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "HX";

  if (hasError || !currentSrc) {
    if (isJovial) {
      return (
        <div className="w-full h-full rounded-full bg-gradient-to-tr from-zinc-950 via-zinc-900 to-amber-950 flex items-center justify-center border border-amber-400/40 text-amber-400 shadow-inner">
          <svg viewBox="0 0 100 100" className="w-6 h-6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="goldMonogram" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFF3A8" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#AA7C11" />
              </linearGradient>
            </defs>
            <circle cx="50" cy="50" r="46" stroke="url(#goldMonogram)" strokeWidth="3" fill="#09090b" />
            <circle cx="50" cy="50" r="40" stroke="url(#goldMonogram)" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            <text x="50" y="58" textAnchor="middle" fill="url(#goldMonogram)" fontFamily="Georgia, serif" fontSize="26" fontWeight="bold" fontStyle="italic">JM</text>
          </svg>
        </div>
      );
    }
    return <span>{initials}</span>;
  }

  return (
    <img
      src={currentSrc}
      alt={name}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover"
      onError={() => {
        if (isJovial && currentSrc !== JOVIAL_GOLD_AVATAR) {
          setCurrentSrc(JOVIAL_GOLD_AVATAR);
        } else {
          setHasError(true);
        }
      }}
    />
  );
};

export const VideoCard: React.FC<VideoCardProps> = ({
  video,
  onClick,
  onEdit,
  onDelete,
  onCreatorClick,
  watchHistory,
}) => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onEdit) onEdit(video);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onDelete) onDelete(video);
  };

  const experimentVariant = useMemo(() => resolveExperimentVariant(video), [video]);
  const displayTitle = experimentVariant?.customTitle || video.title;

  const titleLower = (video.title || (video as any).name || "").toLowerCase();
  const isMargaretLove = titleLower.includes("margaret my love") || video.id === "video-margaret-my-love";
  const isSweetMargaret = titleLower.includes("sweet margaret") || video.id === "cf3e2eec-aa8d-4b49-b091-6dc6a50f6f67";
  const isLoveTonight = titleLower.includes("love tonight") || video.id === "video-love-tonight" || video.id === "vid_love_tonight_01";

  const fallbackPoster = isLoveTonight
    ? "/assets/love-tonight-poster.jpg"
    : isMargaretLove
    ? "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&auto=format&fit=crop&q=80"
    : isSweetMargaret
    ? "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800&auto=format&fit=crop&q=80"
    : undefined;

  const primaryPoster =
    experimentVariant?.thumbnailUrl ||
    video.custom_thumbnail ||
    video.meta?.customThumbnail ||
    video.thumbnailUrl ||
    video.thumbnail ||
    fallbackPoster ||
    (video.uid ? `https://videodelivery.net/${video.uid}/thumbnails/thumbnail.jpg?height=360` : undefined) ||
    (video.streamMediaId ? `https://videodelivery.net/${video.streamMediaId}/thumbnails/thumbnail.jpg?height=360` : undefined);

  const [imgSrc, setImgSrc] = useState<string | undefined>(primaryPoster);
  const [imgError, setImgError] = useState(false);
  const [retryTier, setRetryTier] = useState(0);

  useEffect(() => {
    setImgSrc(primaryPoster);
    setImgError(false);
    setRetryTier(0);
  }, [primaryPoster, video.id, video.uid, video.custom_thumbnail, video.thumbnailUrl, experimentVariant?.thumbnailUrl]);

  const handleImageError = () => {
    const targetUid = video.uid || video.streamMediaId;
    if (retryTier === 0 && fallbackPoster && imgSrc !== fallbackPoster) {
      setRetryTier(1);
      setImgSrc(fallbackPoster);
    } else if (retryTier <= 1 && targetUid && imgSrc !== `https://videodelivery.net/${targetUid}/thumbnails/thumbnail.jpg`) {
      setRetryTier(2);
      setImgSrc(`https://videodelivery.net/${targetUid}/thumbnails/thumbnail.jpg`);
    } else if (retryTier <= 2 && video.thumbnailUrl && video.thumbnailUrl !== imgSrc) {
      setRetryTier(3);
      setImgSrc(video.thumbnailUrl);
    } else if (retryTier <= 3 && video.thumbnail && video.thumbnail !== imgSrc) {
      setRetryTier(4);
      setImgSrc(video.thumbnail);
    } else {
      setImgError(true);
    }
  };

  const computedProgressPct = (() => {
    let lastPos = watchHistory?.lastPosition;
    let dur = watchHistory?.duration || video.duration;

    if (lastPos === undefined || lastPos === null) {
      const vidAny = video as any;
      if (vidAny.watchHistory?.lastPosition) {
        lastPos = vidAny.watchHistory.lastPosition;
        dur = vidAny.watchHistory.duration || dur;
      } else if (vidAny.lastPositionSeconds !== undefined) {
        lastPos = vidAny.lastPositionSeconds;
        dur = vidAny.durationSeconds || dur;
      } else if (typeof localStorage !== "undefined") {
        const localPos =
          localStorage.getItem(`helix_resume_${video.id}`) ||
          (video.uid ? localStorage.getItem(`helix_resume_${video.uid}`) : null) ||
          (video.streamMediaId ? localStorage.getItem(`helix_resume_${video.streamMediaId}`) : null);
        if (localPos) {
          const parsed = parseFloat(localPos);
          if (!isNaN(parsed) && parsed > 0) {
            lastPos = parsed;
          }
        }
      }
    }

    const numericDur = typeof dur === "string" ? parseFloat(dur) : Number(dur);
    if (lastPos && lastPos > 0 && numericDur && numericDur > 0) {
      return Math.min(100, Math.round((lastPos / numericDur) * 100));
    }
    return 0;
  })();

  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!experimentVariant) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    let timer: any = null;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            timer = setTimeout(() => {
              recordImpressionTelemetry(experimentVariant.id, video.id);
            }, 500);
          } else if (timer) {
            clearTimeout(timer);
          }
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [experimentVariant, video.id]);

  const handleCardClick = () => {
    if (experimentVariant) {
      recordClickTelemetry(experimentVariant.id, video.id);
    }
    onClick(video);
  };

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      className="group cursor-pointer flex flex-col space-y-3 font-sans select-none relative"
    >
      {/* 16:9 Thumbnail Container */}
      <div className="relative aspect-video w-full rounded-xl sm:rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800/80 shadow-md group-hover:shadow-emerald-500/10 group-hover:border-emerald-500/50 transition-all duration-300">
        {imgError || !imgSrc ? (
          <div className="w-full h-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-emerald-950/70 flex flex-col justify-end p-4 relative overflow-hidden group-hover:scale-105 transition-transform duration-500">
            <div className="absolute -top-10 -right-10 w-28 h-28 bg-emerald-500/15 rounded-full blur-xl" />
            <p className="text-xs sm:text-sm font-bold text-white line-clamp-2 leading-snug drop-shadow-md z-10">
              {displayTitle}
            </p>
          </div>
        ) : (
          <img
            src={imgSrc}
            alt={displayTitle}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        )}

        {/* Floating Play Overlay Indicator */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-emerald-500 text-zinc-950 flex items-center justify-center shadow-xl transform scale-75 group-hover:scale-100 transition-transform">
            <Play className="w-6 h-6 fill-zinc-950 ml-0.5" />
          </div>
        </div>

        {/* Duration Timestamp Badge / Live Badge */}
        {video.isLive || video.duration === "LIVE" ? (
          <div className="absolute bottom-2.5 right-2.5 bg-red-600 text-white font-mono text-[10px] font-extrabold px-2 py-0.5 rounded-md flex items-center gap-1 shadow-lg">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
            <span>LIVE</span>
          </div>
        ) : (
          <div className="absolute bottom-2.5 right-2.5 bg-black/80 backdrop-blur-sm text-white font-mono text-[11px] font-bold px-2 py-0.5 rounded-md border border-white/10">
            {formatDuration(video.duration)}
          </div>
        )}

        {/* Category Tag */}
        <div className="absolute top-2.5 left-2.5 bg-zinc-950/80 backdrop-blur-sm text-emerald-400 font-mono text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-emerald-500/30">
          {video.category}
        </div>

        {/* YouTube-style red slim progress bar pinned to the bottom edge */}
        {computedProgressPct > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800/90 overflow-hidden z-20">
            <div
              className="h-full bg-red-600 transition-all duration-300"
              style={{ width: `${computedProgressPct}%` }}
            />
          </div>
        )}
      </div>

      {/* Video Info Row */}
      <div className="flex items-start gap-3 px-0.5 justify-between">
        {/* Creator Avatar */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (onCreatorClick) onCreatorClick(video.creatorHandle || video.creatorName, video);
          }}
          className="relative shrink-0 cursor-pointer hover:scale-105 transition-transform"
          title={`View ${video.creatorName}'s channel`}
        >
          <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 p-0.5 shadow-sm">
            <div className="w-full h-full rounded-full bg-zinc-950 flex items-center justify-center overflow-hidden font-bold text-xs text-emerald-400">
              <AvatarWithFallback
                src={video.creatorAvatar}
                name={video.creatorName}
                handle={video.creatorHandle}
                channelId={video.channelId || video.channel_id}
              />
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-1 overflow-hidden flex-1">
          <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-emerald-400 transition-colors line-clamp-2 leading-snug">
            {displayTitle}
          </h3>

          <div
            onClick={(e) => {
              e.stopPropagation();
              if (onCreatorClick) onCreatorClick(video.creatorHandle || video.creatorName, video);
            }}
            className="flex items-center gap-1 text-[11px] text-zinc-400 font-medium cursor-pointer w-fit group/author"
            title={`View ${video.creatorName}'s channel`}
          >
            <span className="truncate group-hover/author:text-emerald-400 transition-colors">{video.creatorName}</span>
            <ChannelBadge
              channelName={video.creatorName}
              channelHandle={video.creatorHandle}
              badge={(video as any).creatorBadge || (video as any).verificationBadge}
              isVerified={true}
              size="xs"
            />
          </div>

          <div className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-500">
            {video.isLive || video.duration === "LIVE" ? (
              <span className="text-red-400 font-bold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                {(video.liveViewerCount || 1420).toLocaleString()} watching now
              </span>
            ) : (
              <>
                <span>{formatLocalizedViews(video.views !== undefined ? video.views : video.viewsCount, t)}</span>
                <span>•</span>
                <span>{formatLocalizedDate(video.uploadDate, t)}</span>
              </>
            )}
          </div>
        </div>

        {/* Options Menu Button */}
        {(onEdit || onDelete) && (
          <div className="relative shrink-0" ref={menuRef}>
            <button
              onClick={handleMenuClick}
              className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
              title="Video Options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div className="absolute right-0 bottom-full mb-1 w-36 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl py-1 z-30 animate-in fade-in zoom-in-95 duration-150">
                {onEdit && (
                  <button
                    onClick={handleEditClick}
                    className="w-full text-left px-3 py-2 text-xs text-zinc-300 hover:text-white hover:bg-zinc-900 flex items-center gap-2 transition-colors"
                  >
                    <Edit3 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Edit Details</span>
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={handleDeleteClick}
                    className="w-full text-left px-3 py-2 text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 flex items-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                    <span>Delete Video</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
